/**
 * RevenueCatBillingProvider — real store billing behind the existing
 * `BillingProvider` seam (types.ts). Written 4 Sept 2026 for the flip.
 *
 * DEPENDS ON AN INTERFACE, NOT THE PACKAGE. `PurchasesLike` below is typed
 * from the MEASURED `.d.ts` of `@revenuecat/purchases-capacitor@11.3.2`
 * (`definitions.d.ts` + `purchases-typescript-internal-esm@17.25.0`), so
 * this file compiles and tests in the pure harness with a fake plugin, and
 * the ONE place the real package is imported is `revenueCatPlugin.ts`.
 *
 * Why 11.3.2 and not 13.x: 12.0.0+ declares `@capacitor/core >=8.0.0`;
 * this app is on Capacitor 7. 11.x is the last line with `>=7.0.0`
 * (MEASURED via `npm view … peerDependencies`, 4 Sept 2026). Upgrade the
 * shell to Capacitor 8 as its own job — before iOS — then move to 13.x.
 *
 * THE INVARIANT THIS FILE MUST NEVER BREAK (SEAM_REGISTRY seam 6):
 *   premium = (store entitlement) OR (lifetime grant)
 * This provider only ever RETURNS a Subscription. It never writes state.
 * Every value it returns passes through `preserveGrant()` in the store
 * before touching `subscription`, so the 19 lifetime testers — who have
 * no store entitlement and never will — keep their access on flip day.
 * `restore()` in particular MUST be routed the same way: it is the call
 * most likely to answer "free" for a granted account.
 */

import type { BillingProvider, PaidPlan, Subscription } from '../types';
import { SCAN_PACK_SIZE } from '../types';
import type { AccountAwareBilling } from './lazyBilling';
import type { StorePrices } from './prices';

// ---------------------------------------------------------------------------
// Product identifiers — MUST match what is created in Play Console
// (Monetise → Products) and attached to the `premium` entitlement in
// RevenueCat. Change here ⇒ change there, same pass.
// ---------------------------------------------------------------------------
export const RC_ENTITLEMENT = 'premium';
export const RC_PRODUCTS = {
  monthly: 'zonein_monthly',
  annual: 'zonein_annual',
  lifetime: 'zonein_lifetime',
  scanPack: 'zonein_scan_pack_15',
} as const;

/** Play application id — must match `android/app/build.gradle` applicationId. */
export const PLAY_PACKAGE = 'com.zoneinai.app';

/**
 * Google's documented deep link into the Play subscription centre. Stores
 * do not let an app cancel a subscription; this is where the user does it.
 * With a `sku` the page opens on that subscription directly.
 */
export function playSubscriptionsUrl(sku?: string): string {
  const base = 'https://play.google.com/store/account/subscriptions';
  const q = new URLSearchParams({ package: PLAY_PACKAGE });
  if (sku) q.set('sku', sku);
  return `${base}?${q.toString()}`;
}

/** The Play product id behind a plan label, for the subscription-centre link. */
export function productIdForPlan(plan: PaidPlan | 'free' | 'trial'): string | undefined {
  if (plan === 'monthly') return RC_PRODUCTS.monthly;
  if (plan === 'annual') return RC_PRODUCTS.annual;
  if (plan === 'lifetime') return RC_PRODUCTS.lifetime;
  return undefined;
}

/** The subset of the plugin this provider uses — typed from the real .d.ts. */
export interface RcEntitlementInfo {
  readonly isActive: boolean;
  readonly willRenew: boolean;
  /** "NORMAL" | "INTRO" | "TRIAL" | "PREPAID" (string in the .d.ts). */
  readonly periodType: string;
  readonly productIdentifier: string;
  readonly originalPurchaseDateMillis: number;
  readonly latestPurchaseDateMillis: number;
  readonly expirationDateMillis: number | null;
}
export interface RcCustomerInfo {
  readonly entitlements: {
    readonly active: { readonly [id: string]: RcEntitlementInfo };
    readonly all: { readonly [id: string]: RcEntitlementInfo };
  };
  readonly activeSubscriptions: string[];
  readonly allPurchasedProductIdentifiers: string[];
}
export interface RcPackage {
  readonly identifier: string;
  readonly product: {
    readonly identifier: string;
    /** Formatted base price (Play: the default option's full price; App Store: the product price). */
    readonly priceString: string;
    /** Introductory offer, when the store applies one to this customer (e.g. founding $19.99 first year). */
    readonly introPrice?: { readonly priceString: string; readonly cycles: number; readonly period: string } | null;
  };
}
/**
 * Price labels for the paywall, straight from the store (PURE; unit-tested).
 * The app's `PLANS` constants are the FALLBACK, not the truth — during a
 * founding window the store price differs (Play sheet $19.99 vs card
 * $29.99, MEASURED 28 Sept 2026). Returns only the plans found.
 */
export function pricesFromOfferings(offerings: RcOfferings): StorePrices {
  const packages = [
    ...(offerings.current?.availablePackages ?? []),
    ...Object.values(offerings.all).flatMap((o) => o.availablePackages),
  ];
  const out: StorePrices = {};
  const label = (plan: PaidPlan, pkg: RcPackage): string => {
    const base = pkg.product.priceString;
    const intro = pkg.product.introPrice?.priceString;
    if (plan === 'annual') return intro ? `${intro} first year, then ${base}/yr` : `${base}/yr`;
    if (plan === 'monthly') return intro ? `${intro} first month, then ${base}/mo` : `${base}/mo`;
    return base;
  };
  for (const plan of ['annual', 'monthly', 'lifetime'] as const) {
    const pid = productIdForPlan(plan);
    if (!pid) continue;
    const pkg = packages.find((p) => matchesProduct(p.product.identifier, pid));
    if (pkg && !out[plan]) out[plan] = label(plan, pkg);
  }
  // The consumable scan pack (17 Oct 2026): until now a hard-coded "$0.99".
  const scan = packages.find((p) => matchesProduct(p.product.identifier, RC_PRODUCTS.scanPack));
  if (scan) out.scanPack = scan.product.priceString;
  return out;
}

export interface RcOfferings {
  readonly current: { readonly availablePackages: RcPackage[] } | null;
  readonly all: { readonly [id: string]: { readonly availablePackages: RcPackage[] } };
}
export interface RcError {
  code?: string | number;
  message?: string;
  userCancelled?: boolean | null;
  readableErrorCode?: string;
}
export interface PurchasesLike {
  configure(cfg: { apiKey: string; appUserID?: string }): Promise<void>;
  isConfigured(): Promise<{ isConfigured: boolean }>;
  logIn(opts: { appUserID: string }): Promise<{ customerInfo: RcCustomerInfo; created: boolean }>;
  logOut(): Promise<{ customerInfo: RcCustomerInfo }>;
  getOfferings(): Promise<RcOfferings>;
  purchasePackage(opts: { aPackage: RcPackage }): Promise<{
    productIdentifier: string;
    customerInfo: RcCustomerInfo;
  }>;
  restorePurchases(): Promise<{ customerInfo: RcCustomerInfo }>;
  getCustomerInfo(): Promise<{ customerInfo: RcCustomerInfo }>;
  addCustomerInfoUpdateListener(cb: (info: RcCustomerInfo) => void): Promise<unknown>;
  /** Optional: load the underlying plugin module without calling it, so "load" is a step of its own in the trace. */
  preload?(): Promise<void>;
}

/** Thrown when the user backed out of the store sheet — not a failure. */
export class PurchaseCancelledError extends Error {
  readonly cancelled = true;
  constructor() {
    super('Purchase cancelled');
    this.name = 'PurchaseCancelledError';
  }
}

/** RevenueCat's own code for a user-cancelled purchase (errors.d.ts:7). */
const PURCHASE_CANCELLED_CODE = '1';

export function isCancelled(err: unknown): boolean {
  if (err instanceof PurchaseCancelledError) return true;
  const e = err as RcError | null;
  if (!e || typeof e !== 'object') return false;
  return e.userCancelled === true || String(e.code) === PURCHASE_CANCELLED_CODE;
}

/** One human sentence from whatever the plugin threw — verbatim where possible (CLAUDE.md §2). */
export function describeBillingError(err: unknown): string {
  if (isCancelled(err)) return 'Purchase cancelled — nothing was charged.';
  const e = err as RcError | Error | null;
  const msg =
    (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message) ||
    String(err);
  const code =
    e && typeof e === 'object' && 'readableErrorCode' in e && e.readableErrorCode
      ? ` (${e.readableErrorCode})`
      : '';
  return `Store error${code}: ${msg}`;
}

/**
 * Map RevenueCat's customer info onto the app's `Subscription`.
 *
 * PURE and exported so it is unit-tested directly. `current` is only used
 * to carry `trialStartedAt` forward when the store has nothing to say — the
 * grant fields are NOT this function's job (preserveGrant owns them).
 */
export function subscriptionFromCustomerInfo(
  info: RcCustomerInfo,
  current: Subscription,
): Subscription {
  const ent = info.entitlements.active[RC_ENTITLEMENT];
  if (!ent || !ent.isActive) {
    // No active entitlement. Say 'free' honestly; preserveGrant will keep a
    // grant holder premium regardless of this answer.
    return { plan: 'free', trialStartedAt: current.trialStartedAt };
  }
  const pid = ent.productIdentifier;
  if (pid === RC_PRODUCTS.lifetime || pid.includes('lifetime')) {
    return { plan: 'lifetime', trialStartedAt: current.trialStartedAt };
  }
  const paid: PaidPlan =
    pid === RC_PRODUCTS.annual || pid.includes('annual')
      ? 'annual'
      : pid.includes('quarterly')
        ? 'quarterly'
        : 'monthly'; // unknown product still active ⇒ the most conservative paid label
  if (ent.periodType === 'TRIAL') {
    // In the store's free-trial period: the app's own trial state, so the
    // existing TrialBanner / countdown keep working unchanged.
    return {
      plan: 'trial',
      trialStartedAt: ent.originalPurchaseDateMillis || current.trialStartedAt || Date.now(),
      pendingPlan: paid,
    };
  }
  return { plan: paid, trialStartedAt: current.trialStartedAt };
}

export interface RevenueCatOptions {
  /** Public SDK key for THIS platform (Play key on Android, App Store key on iOS). Public by design, like the Firebase web key. */
  apiKey: string;
  /** Called whenever RevenueCat reports a change (renewal, expiry, refund, restore on another device). Route it through preserveGrant. */
  onRemoteUpdate?: (incoming: Subscription) => void;
  now?: () => number;
  /**
   * Instrumentation (CLAUDE.md §2): one line per billing step — start,
   * ok (with ms), or the verbatim error. The store shows the last few in
   * Settings so a step that never returns is visible as "started, never
   * finished" instead of an infinite spinner (build 18, 27 Sept 2026:
   * "Opening Google Play…" sat forever with nothing anywhere).
   */
  onStep?: (line: string) => void;
  /** Per-step watchdog overrides in ms (tests use tiny values). */
  timeouts?: Partial<Record<BillingStep, number>>;
}

export type BillingStep =
  | 'load plugin'
  | 'isConfigured'
  | 'configure'
  | 'listener'
  | 'getOfferings'
  | 'purchase'
  | 'restore'
  | 'getCustomerInfo'
  | 'logIn'
  | 'logOut';

/**
 * Watchdog per step. The Play purchase sheet itself is exempt (a person
 * may sit in it for minutes); everything else must answer or be named.
 */
export const DEFAULT_STEP_TIMEOUTS: Record<BillingStep, number> = {
  'load plugin': 15_000,
  isConfigured: 10_000,
  configure: 20_000,
  listener: 10_000,
  getOfferings: 30_000,
  purchase: 0, // no watchdog — the user is in Google Play's own UI
  restore: 30_000,
  getCustomerInfo: 20_000,
  logIn: 20_000,
  logOut: 10_000,
};

export class BillingStepTimeoutError extends Error {
  constructor(
    readonly step: BillingStep,
    readonly ms: number,
  ) {
    super(`Billing step "${step}" did not respond within ${Math.round(ms / 1000)}s.`);
    this.name = 'BillingStepTimeoutError';
  }
}

/** Race a step against its watchdog. `ms <= 0` means no watchdog. */
export function withTimeout<T>(p: Promise<T>, ms: number, step: BillingStep): Promise<T> {
  if (!(ms > 0)) return p;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new BillingStepTimeoutError(step, ms)), ms);
  });
  return Promise.race([p, guard]).finally(() => clearTimeout(timer));
}

/**
 * Google Play subscriptions reach the SDK as `productId:basePlanId`
 * (StoreProduct.id, documented: "Google Sub: 'productId:basePlanID'"), so
 * `zonein_annual` arrives as `zonein_annual:annual`. One-time products and
 * the entitlement's productIdentifier carry the bare id. Match both — an
 * exact-only match threw "not in any RevenueCat offering" for every
 * subscription (found 26 Sept 2026, before any device saw it).
 */
export function matchesProduct(storeIdentifier: string, productId: string): boolean {
  return storeIdentifier === productId || storeIdentifier.startsWith(`${productId}:`);
}

export class RevenueCatBillingProvider implements BillingProvider, AccountAwareBilling {
  readonly name = 'RevenueCat (Google Play)';
  private configured: Promise<void> | null = null;
  private listenerAttached = false;

  constructor(
    private readonly purchases: PurchasesLike,
    private readonly getState: () => Subscription,
    private readonly opts: RevenueCatOptions,
  ) {}

  /** Run one step under its watchdog and narrate it (start / ok+ms / error). */
  private async step<T>(name: BillingStep, run: () => Promise<T>): Promise<T> {
    const note = this.opts.onStep;
    const now = this.opts.now ?? Date.now;
    const ms = this.opts.timeouts?.[name] ?? DEFAULT_STEP_TIMEOUTS[name];
    const t0 = now();
    note?.(`${name}: started`);
    try {
      const out = await withTimeout(run(), ms, name);
      note?.(`${name}: ok (${Math.max(0, now() - t0)} ms)`);
      return out;
    } catch (err) {
      note?.(`${name}: FAILED after ${Math.max(0, now() - t0)} ms — ${describeBillingError(err)}`);
      throw err;
    }
  }

  /** Lazy, idempotent. Never throws synchronously — a bad key surfaces as a readable error on first use. */
  private ensureConfigured(): Promise<void> {
    if (!this.configured) {
      this.configured = (async () => {
        if (!this.opts.apiKey) {
          throw new Error(
            'RevenueCat is not configured on this build (VITE_REVENUECAT_ANDROID_KEY missing).',
          );
        }
        if (this.purchases.preload) {
          // Load the plugin module as a step of its own; one retry (§1 fallback).
          try {
            await this.step('load plugin', () => this.purchases.preload!());
          } catch {
            await this.step('load plugin', () => this.purchases.preload!());
          }
        }
        // isConfigured is a trivial native call. If it does not answer, do
        // NOT stall the whole flow on it — configure anyway (RevenueCat
        // tolerates a repeat configure with a warning). §1: fallback path.
        let already = false;
        try {
          already = (await this.step('isConfigured', () => this.purchases.isConfigured())).isConfigured;
        } catch (err) {
          if (!(err instanceof BillingStepTimeoutError)) throw err;
          this.opts.onStep?.('isConfigured: timed out — configuring anyway (fallback)');
        }
        if (!already) {
          await this.step('configure', () => this.purchases.configure({ apiKey: this.opts.apiKey }));
        }
        if (!this.listenerAttached && this.opts.onRemoteUpdate) {
          this.listenerAttached = true;
          const cb = this.opts.onRemoteUpdate;
          await this.step('listener', () =>
            this.purchases.addCustomerInfoUpdateListener((info) => {
              cb(subscriptionFromCustomerInfo(info, this.getState()));
            }),
          );
        }
      })().catch((e) => {
        this.configured = null; // allow a retry after a transient failure
        this.listenerAttached = false;
        throw e;
      });
    }
    return this.configured;
  }

  /**
   * Diagnostics without a purchase: configure and fetch offerings, narrating
   * every step. Returns the package identifiers seen, so Settings can show
   * "offerings: 4 packages" — or the exact step that never came back.
   */
  async selfTest(): Promise<string[]> {
    await this.ensureConfigured();
    const offerings = await this.step('getOfferings', () => this.purchases.getOfferings());
    const ids = [
      ...(offerings.current?.availablePackages ?? []),
      ...Object.values(offerings.all).flatMap((o) => o.availablePackages),
    ].map((p) => p.product.identifier);
    const unique = Array.from(new Set(ids));
    this.opts.onStep?.(`offerings: ${unique.length} product(s) — ${unique.join(', ') || 'NONE'}`);
    return unique;
  }

  /** Store price labels for the paywall (see pricesFromOfferings). Never throws — the paywall falls back to PLANS. */
  async storePrices(): Promise<StorePrices> {
    try {
      await this.ensureConfigured();
      const offerings = await this.step('getOfferings', () => this.purchases.getOfferings());
      return pricesFromOfferings(offerings);
    } catch {
      return {};
    }
  }

  /**
   * Tie the store customer to the Firebase uid so entitlements follow the
   * ACCOUNT across devices and onto iOS — not the install. Called from the
   * auth listener; a no-op if not configured (no key, web).
   */
  async identify(uid: string): Promise<Subscription | null> {
    try {
      await this.ensureConfigured();
      const { customerInfo } = await this.step('logIn', () => this.purchases.logIn({ appUserID: uid }));
      return subscriptionFromCustomerInfo(customerInfo, this.getState());
    } catch {
      return null;
    }
  }

  async forget(): Promise<void> {
    try {
      await this.ensureConfigured();
      await this.step('logOut', () => this.purchases.logOut());
    } catch {
      /* signing out must never fail on billing */
    }
  }

  /**
   * Boot-time / foreground read of the current entitlement. Belt AND braces
   * with the update listener (CLAUDE.md §1: never a single path) — this is
   * what keeps a renewed or refunded subscription honest on an install that
   * never signs in. Null on any failure; the caller treats null as "no news".
   */
  async refresh(): Promise<Subscription | null> {
    try {
      await this.ensureConfigured();
      const { customerInfo } = await this.step('getCustomerInfo', () => this.purchases.getCustomerInfo());
      return subscriptionFromCustomerInfo(customerInfo, this.getState());
    } catch {
      return null;
    }
  }

  private async findPackage(productId: string): Promise<RcPackage> {
    const offerings = await this.step('getOfferings', () => this.purchases.getOfferings());
    const pools = [
      offerings.current?.availablePackages ?? [],
      ...Object.values(offerings.all).map((o) => o.availablePackages),
    ];
    for (const pkgs of pools) {
      const hit = pkgs.find((p) => matchesProduct(p.product.identifier, productId));
      if (hit) return hit;
    }
    throw new Error(
      `Product "${productId}" is not in any RevenueCat offering — check the store products (App Store Connect / Play Console) and the RevenueCat offering.`,
    );
  }

  async subscribe(plan: PaidPlan): Promise<Subscription> {
    await this.ensureConfigured();
    const productId =
      plan === 'lifetime'
        ? RC_PRODUCTS.lifetime
        : plan === 'annual'
          ? RC_PRODUCTS.annual
          : plan === 'monthly'
            ? RC_PRODUCTS.monthly
            : null;
    if (!productId) throw new Error(`Plan "${plan}" is no longer sold.`);
    const pkg = await this.findPackage(productId);
    try {
      const { customerInfo } = await this.step('purchase', () =>
        this.purchases.purchasePackage({ aPackage: pkg }),
      );
      return subscriptionFromCustomerInfo(customerInfo, this.getState());
    } catch (err) {
      if (isCancelled(err)) throw new PurchaseCancelledError();
      throw err;
    }
  }

  /**
   * Stores do not let an app cancel a subscription. The honest answer is the
   * CURRENT entitlement state (unchanged) — the UI must send the user to
   * Play's subscription centre. Returning the truth here means the button
   * can never silently downgrade anyone.
   */
  async cancel(): Promise<Subscription> {
    await this.ensureConfigured();
    const { customerInfo } = await this.step('getCustomerInfo', () => this.purchases.getCustomerInfo());
    return subscriptionFromCustomerInfo(customerInfo, this.getState());
  }

  async restore(): Promise<Subscription | null> {
    await this.ensureConfigured();
    const { customerInfo } = await this.step('restore', () => this.purchases.restorePurchases());
    return subscriptionFromCustomerInfo(customerInfo, this.getState());
  }

  async purchaseScanPack(): Promise<number> {
    await this.ensureConfigured();
    const pkg = await this.findPackage(RC_PRODUCTS.scanPack);
    try {
      await this.step('purchase', () => this.purchases.purchasePackage({ aPackage: pkg }));
      return SCAN_PACK_SIZE;
    } catch (err) {
      if (isCancelled(err)) throw new PurchaseCancelledError();
      throw err;
    }
  }
}

/**
 * Web with billing LIVE: the Capacitor plugin has no web implementation
 * ("Web not supported in this plugin." — web.js:8, MEASURED). The mock
 * must NOT be used here — it would hand out premium for free, which is the
 * exact failure STORE_BILLING_LIVE exists to prevent. So every purchase
 * surface fails loudly with the one honest instruction.
 */
export const WEB_BILLING_MESSAGE =
  'Subscriptions are purchased in the Android app. Install ZoneIn AI from Google Play, sign in with the same account, and your access follows you here.';

export class WebBillingUnavailableProvider implements BillingProvider {
  readonly name = 'Store billing (not available on web)';
  async subscribe(_plan: PaidPlan): Promise<Subscription> {
    throw new Error(WEB_BILLING_MESSAGE);
  }
  async cancel(): Promise<Subscription> {
    throw new Error(WEB_BILLING_MESSAGE);
  }
  async restore(): Promise<Subscription | null> {
    throw new Error(WEB_BILLING_MESSAGE);
  }
  async purchaseScanPack(): Promise<number> {
    throw new Error(WEB_BILLING_MESSAGE);
  }
}
